import React, { useEffect, useState, useReducer, useRef } from "react";
import { isMobile } from "react-device-detect";
import ProjectHeader from "./ProjectHeader";
import { getProjectSummary } from "./service";
import { Outlet, useParams, useLocation, useNavigate } from "react-router-dom";
import { CustomizationSpinnerContainer, Notification } from "../common/style";
import { isBrowser } from "react-device-detect";
import { CarpenterView } from "../carpenter-view";
import { getRoomViewContext, saveObjectPositions } from "../product/service";
import ProjectSummary from "./ProjectSummary";
import { getCurrentUser } from "../auth/service";
import { Spinner } from "reactstrap";
import NewProjectSummary from "./NewProjectSummary";

const Project = () => {
  const params = useParams();
  const projectId = params.projectId;
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [projectLoaded, setProjectLoaded] = useState(false);
  const [notification, setNotification] = useState({ type: "", message: "" });
  const [isNotificationVisible, setIsNotificationVisible] = useState(true);
  const currentUser = getCurrentUser();
  const [projectSummary, setProjectSummary] = useState(null);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [currentGroup, setCurrentGroup] = useState({});
  const [currentSelection, setCurrentSelection] = useState([]);
  const [currentTab, setCurrentTab] = useState("info");
  const [isReset, setIsReset] = useState(false);
  const [hoverItem, setHoverItem] = useState(null);
  const history = useRef({ position: -1, changes: [] });
  const navigate = useNavigate();
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const currentRoomRef = useRef(currentRoom);
  const [finalisedContexts, setFinalisedContexts] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isDesigner, setIsDesigner] = useState(false);
  const [isViewer, setIsViewer] = useState(false);
  const [isOrderFinalised, setIsOrderFinalised] = useState(false);
  const [isOrderDraft, setIsOrderDraft] = useState(false);
  const [access, setAccess] = useState({});
  const [lightsList, setLightsList] = useState({});
  const [version, setVersion] = useState(1);

  const onDismissNotification = () => {
    setIsNotificationVisible(false);
  };

  const loadProject = ({ fromImportProject = false } = {}) => {
    setIsLoading(true);

    getProjectSummary(projectId)
      .then((summary) => {
        setProjectSummary(summary);
        setVersion(summary.app_version == "1" ? 1 : 2);
        const defaultRoomId = summary.rooms[0].id;
        const roomId =
          !fromImportProject && params.roomId
            ? params.roomId
            : summary.rooms[0].id;
        loadRoom(roomId, null, null, true, defaultRoomId);
        setIsAdmin(summary.is_admin);
        setIsOrderFinalised(summary.is_order_finalised);
        setIsOrderDraft(summary.is_order_draft);
        const ownerStatus = isOwner(summary);
        const viewerStatus = isNotOwner(summary);
        setIsDesigner(ownerStatus);
        setIsViewer(viewerStatus);
        setAccess(summary.access);
        setFinalisedContexts(summary.finalised_contexts);
        setLightsList(summary.lights_list);
      })
      .finally(() => setIsLoading(false));
  };

  const isOwner = (projectSummary) => {
    return projectSummary.owner.id === currentUser.id;
  };

  const isNotOwner = (projectSummary) => {
    return projectSummary.owner.id != currentUser.id;
  };

  const refreshProjectSummary = () => {
    getProjectSummary(projectId).then((summary) => {
      setProjectSummary(summary);
    });
  };

  const getRoomViewContextWithRetry = async ({
    projectId,
    defaultRoomId,
    roomId,
  }) => {
    try {
      const roomResponse = await getRoomViewContext({ projectId, roomId });
      return roomResponse;
    } catch (error) {
      console.log(`Retrying with defaultRoomId: ${error.message}`);
      return getRoomViewContext({ projectId, roomId: defaultRoomId });
    }
  };

  const loadRoom = async (
    roomId,
    addedModuleId = null,
    cloneItems = null,
    fromCopyPate = false,
    skipSummary = false,
    skipSummaryLoad = false,
    defaultRoomId = null,
    showLoader = false
  ) => {
    if (showLoader) setIsLoading(true);

    if (!skipSummary) refreshProjectSummary();

    return getRoomViewContextWithRetry({
      projectId: projectId,
      roomId,
      defaultRoomId,
    })
      .then((roomResponse) => {
        // Setting position and dimension for groups
        roomResponse.groups.forEach((group) => {
          group.position = selectionManager.calculateGroupPosition(
            group.modules.concat(group.walls)
          );

          if (group.category === "BasicGroup")
            group.dimensions = selectionManager.calculateGroupDimensions(
              group.modules.concat(group.walls)
            );
        });

        setCurrentRoom(roomResponse);

        const room = roomResponse;
        const selections = selectionManager.selections();

        const groupSelections = [];
        const moduleSelections = [];
        const wallSelections = [];
        const moduleSelectionsInsideGroup = [];
        if (addedModuleId) {
          let item = roomResponse.modules.find(
            (module) => module.id === addedModuleId
          );
          selectionManager.clear();
          selectionManager.select(item);
          return;
        }
        selections.forEach((item) => {
          if (item.type === "group") {
            groupSelections.push(item);
          } else if (item.type === "module" && !item.group_id) {
            moduleSelections.push(item);
          } else if (item.type === "module" && item.group_id) {
            moduleSelectionsInsideGroup.push(item);
          } else if (item.type === "wall") {
            wallSelections.push(item);
          }
        });

        const selectionsToAdd = [];

        groupSelections.forEach((group) => {
          const matchingGroup = room.groups.find((grp) => grp.id === group.id);
          if (matchingGroup) {
            selectionsToAdd.push(matchingGroup);
          }
        });

        moduleSelectionsInsideGroup.forEach((module) => {
          let matchingModuleInGroup = null;

          room.groups.forEach((group) => {
            const foundModule = group.modules.find(
              (mod) => mod.id === module.id
            );
            if (foundModule) {
              matchingModuleInGroup = foundModule;
            }
          });

          if (matchingModuleInGroup) {
            selectionsToAdd.push(matchingModuleInGroup);
          }
        });

        wallSelections.forEach((wall) => {
          const matchingWall = room.walls.find((wl) => wl.id === wall.id);
          if (matchingWall) {
            selectionsToAdd.push(matchingWall);
          }
        });

        moduleSelections.forEach((module) => {
          const matchingModule = room.modules.find(
            (mod) => mod.id === module.id
          );
          if (matchingModule) {
            selectionsToAdd.push(matchingModule);
          }
        });

        const uniqueSelections = Array.from(
          new Set(selectionsToAdd.map((selection) => selection.id))
        ).map((id) => {
          return selectionsToAdd.find((selection) => selection.id === id);
        });
        if (!fromCopyPate) {
          if (cloneItems) {
            let selections = selectionManager.selections();
            let copied_objects = [];
            cloneItems.forEach((ci) => {
              if (ci.type === "module") {
                const module = roomResponse.modules.find((m) => m.id === ci.id);
                if (module) {
                  copied_objects.push(module);
                }
              } else if (ci.type === "group") {
                const group = roomResponse.groups.find((g) => g.id === ci.id);
                if (group) {
                  copied_objects.push(group);
                }
              } else if (ci.type === "wall") {
                const wall = roomResponse.walls.find((w) => w.id === ci.id);
                if (wall) {
                  copied_objects.push(wall);
                }
              }
            });

            selections.forEach((item) => {
              selectionManager.remove(item);
            });

            setCurrentSelection(copied_objects);
          } else if (uniqueSelections.length > 0) {
            setCurrentSelection(uniqueSelections);
          }
        }

        if (
          location.pathname == `/projects/${projectId}` ||
          (location.pathname.startsWith(`/projects/${projectId}/rooms`) &&
            !location.pathname.startsWith(
              `/projects/${projectId}/rooms/${roomResponse.id}/canvas`
            ))
        ) {
          return navigate(`rooms/${roomResponse.id}/canvas`);
        }
      })
      .catch(({ error }) => {
        setIsNotificationVisible(true);
        setNotification({ type: "error", message: error });
      })
      .finally(() => {
        if (showLoader) {
          setIsLoading(false);
        }

        setProjectLoaded(true);
      });
  };

  const reloadRoom = (skipSummary = false) => {
    return loadRoom(currentRoom.id, null, null, false, skipSummary);
  };

  useEffect(() => {
    loadProject();
  }, []);

  useEffect(() => {
    currentRoomRef.current = currentRoom;
    if (currentRoom) {
      setCurrentGroup(currentRoom.groups[0]);
    }
  }, [currentRoom]);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  function isEq(object1, object2) {
    if (!object1 || !object2 || object1.type != object2.type) {
      return false;
    }

    if (
      object1.type === "group" ||
      object1.type === "module" ||
      object1.type === "wall"
    ) {
      return object1.id == object2.id;
    } else if (object1.type === "shutter") {
      return (
        object1.sequence_number == object2.sequence_number &&
        object1.order_item_id == object2.order_item_id
      );
    } else if (object1.type === "partition") {
      return (
        object1.path == object2.path &&
        object1.order_item_id == object2.order_item_id
      );
    } else if (object1.type === "space") {
      return (
        object1.path == object2.path &&
        object1.order_item_id == object2.order_item_id
      );
    } else {
      return false;
    }
  }

  const selectionManager = {
    selectMultiple: (items) => setCurrentSelection(items),
    select: (item) => {
      selectionManager.selectMultiple([item]);
    },
    add: (item) => {
      if (selectionManager.isSelected(item)) {
        return;
      }

      const newSelection = currentSelection.concat([item]);
      setCurrentSelection(newSelection);
    },
    getKey: () => {
      return currentSelection.map((item) => getObjectKey(item)).join(",");
    },
    remove: (item) => {
      const newCurrentSelection = currentSelection.filter(
        (curItem) => !isEq(curItem, item)
      );
      setCurrentSelection(newCurrentSelection);
    },
    toggle: (item, isMultiSelect) => {
      if (isMultiSelect) {
        let batchedNewSelections = selectionManager.selections();
        // toggle logic
        if (selectionManager.isSelected(item))
          batchedNewSelections = batchedNewSelections.filter(
            (curItem) => !isEq(curItem, item)
          );
        else batchedNewSelections = batchedNewSelections.concat([item]);

        const groupIds = batchedNewSelections
          .filter((item) => item.type === "group")
          .map((item) => item.id);

        const isSelectionHomogenous = (selections) =>
          selections.every((obj) => obj.type == item.type);

        const isSelectionsBelongtoSameModule = (selections) =>
          selections.every((obj) => obj.order_item_id == item.order_item_id);

        for (let index = batchedNewSelections.length - 1; index >= 0; index--) {
          let sel = batchedNewSelections[index];

          // If selection is of type space/shutter/partition/etc
          if (["group", "wall", "module"].includes(sel.type) === false) {
            // Unless all the selections belong to the same module and are of same type, then set item to module
            if (
              !isSelectionHomogenous(batchedNewSelections) ||
              !isSelectionsBelongtoSameModule(batchedNewSelections)
            ) {
              const module = selectionManager.findModule(sel.order_item_id);
              if (module) {
                sel = module;
                // If module already exists, delete the sel
                if (batchedNewSelections.find((e) => isEq(e, module)))
                  batchedNewSelections.splice(index, 1);
                // If module doesn't already exist, delete the sel and add the module
                else batchedNewSelections.splice(index, 1, module);
              }
            }
          }

          // If the selection is a group, remove all modules that are part of that group
          if (sel.type === "module" && groupIds.includes(sel.group_id))
            batchedNewSelections.splice(index, 1);
        }
        setCurrentSelection(batchedNewSelections);
      } else {
        if (!selectionManager.isSelected(item)) selectionManager.select(item);
      }
    },
    clear: () => {
      setCurrentSelection([]);
    },
    isEmpty: () => {
      return currentSelection.length === 0;
    },
    isSelected: (item) => {
      return !!currentSelection.find((curItem) => isEq(item, curItem));
    },
    selections: () => {
      return currentSelection;
    },
    selectionIds: () => {
      return currentSelection.flatMap((selection) => {
        if (selection.type === "module") {
          return selection.id;
        } else if (selection.type === "group") {
          return selection.modules?.map((module) => module.id) || [];
        }
        return [];
      });
    },
    selection: () => {
      return currentSelection.length === 1 ? currentSelection[0] : null;
    },
    isMultiSelection: () => currentSelection.length > 1,
    selectionOfType: (type) => {
      const item = selectionManager.selection();
      return item && item.type === type ? item : null;
    },
    selectionsOfType: (type) => {
      return (
        selectionManager.selections().length > 0 &&
        selectionManager
          .selections()
          .every(
            (obj) =>
              obj.type === (type || selectionManager.selections()[0].type)
          )
      );
    },
    selectedGroup: () => selectionManager.selectionOfType("group"),
    selectedWall: () => selectionManager.selectionOfType("wall"),
    selectedModule: () => selectionManager.selectionOfType("module"),
    selectedSpace: () => selectionManager.selectionOfType("space"),
    selectedPartition: () => selectionManager.selectionOfType("partition"),
    selectedShutter: () => selectionManager.selectionOfType("shutter"),
    // Returns the module that is the parent of the current internal selection (space, partition, etc.,)
    activeModule: () => {
      if (selectionManager.selectedGroup()) {
        return null;
      } else if (selectionManager.selectedModule()) {
        return selectionManager.selectedModule();
      } else if (selectionManager.selectedSpace()) {
        return selectionManager.findModule(
          selectionManager.selectedSpace().order_item_id
        );
      } else if (selectionManager.selectedPartition()) {
        return selectionManager.findModule(
          selectionManager.selectedPartition().order_item_id
        );
      } else if (selectionManager.selectedShutter()) {
        return selectionManager.findModule(
          selectionManager.selectedShutter().order_item_id
        );
      } else {
        return null;
      }
    },
    findGroup: (groupId) => {
      return currentRoom.groups.find((group) => group.id === groupId);
    },
    findGroupByTempId: (tempId) => {
      return currentRoom.groups.find((group) => group.temp_id === tempId);
    },
    findModuleByTempId: (tempId) => {
      return currentRoom.modules.find((module) => module.temp_id === tempId);
    },
    findWall: (wallId) => {
      if (wallId == null) return null;

      const wall = currentRoom.walls.find((wall) => wall.id == wallId);

      if (wall) return wall;

      for (const group of currentRoom.groups)
        for (const wall of group.walls) if (wall.id === wallId) return wall;

      return null;
    },
    findModule: (moduleId) => {
      if (moduleId == null) {
        return null;
      }

      const module = currentRoom.modules.find((e) => e.id == moduleId);
      if (module) return module;

      for (const group of currentRoom.groups) {
        for (const module of group.modules) {
          if (module.id === moduleId) {
            return module;
          }
        }
      }

      return null;
    },
    findSpace: (spacePath, moduleId) => {
      const allChildSpaces = (space) => {
        if (space == null) {
          return;
        }
        let childSpaces = [space];
        if (space.children) {
          space.children.forEach((child) => {
            childSpaces.push(child);
            childSpaces.push(allChildSpaces(child));
          });
        }
        return childSpaces.flat();
      };

      const module = selectionManager.findModule(moduleId);
      return allChildSpaces(module.template.root_space).find(
        (space) => space.path === spacePath
      );
    },
    findPartition: (path, moduleId) => {
      const module = selectionManager.findModule(moduleId);

      return module.template.partitions.find(
        (partition) => partition.path == path
      );
    },
    findShutter: (sequenceNumber, moduleId) => {
      const module = selectionManager.findModule(moduleId);

      return module.template.shutters.find(
        (shutter) => shutter.sequence_number == sequenceNumber
      );
    },
    activeModuleId: () => {
      return selectionManager.activeModule()?.id;
    },
    activeGroupId: () => {
      return selectionManager.activeGroup()?.id;
    },
    activeGroup: () => {
      if (selectionManager.selectedGroup()) {
        return selectionManager.selectedGroup();
      } else {
        const module = selectionManager.activeModule();
        return module ? selectionManager.findGroup(module.group_id) : null;
      }
    },
    triggerLoadRoom: async (
      roomId,
      addedModuleId = null,
      cloneItems = null,
      fromCopyPate = false
    ) => {
      await loadRoom(roomId, addedModuleId, cloneItems, fromCopyPate);
    },
    areSelectionsModules: () => selectionManager.selectionsOfType("module"),
    areSelectionsGroups: () => selectionManager.selectionsOfType("group"),
    areSelectionsWalls: () => selectionManager.selectionsOfType("wall"),
    areSelectionsGroupsAndModules: () => {
      const selectedItems = selectionManager.selections();
      const hasGroups = selectedItems.some((item) => item.type === "group");
      const hasModules = selectedItems.some((item) => item.type === "module");

      return hasGroups && hasModules;
    },
    currentRoom: () => currentRoom,
    areSelectionsHomogenous: (type) => selectionManager.selectionsOfType(type),
    isOrderItemSelected: (id) => {
      return selectionManager.selections().some((e) => {
        if (e.type == "module") {
          if (e.id == id) return true;
        } else if (["shutter", "space", "partition"].includes(e.type))
          if (e.order_item_id == id) return true;

        return false;
      });
    },
    areSelectionsCanDelete: () => {
      return selectionManager.areSelectionsHomogenous("shutter") ||
        selectionManager.areSelectionsHomogenous("space") ||
        selectionManager.areSelectionsHomogenous("partition")
        ? false
        : true;
    },
    canSelectionsAlign: () => {
      return selectionManager.selections().every((e) => {
        const group =
          e.type == "module" && e.group_id
            ? selectionManager.findGroup(e.group_id)
            : null;

        if (
          ["group", "wall"].includes(e.type) ||
          (e.type == "module" && !e.group_id) ||
          (group && group.category === "BasicGroup")
        )
          return true;

        return false;
      });
    },
    canSelectionsAlignInGroup: () => {
      const groupId = selectionManager.selections()[0].group_id;

      return selectionManager.selections().every((e) => e.group_id === groupId);
    },
    calculateGroupPosition: (items) => {
      const hasItems = items.length > 0;

      return {
        x: hasItems ? Math.min(...items.map((m) => m.position.x)) : 0,
        y: hasItems ? Math.min(...items.map((m) => m.position.y)) : 0,
      };
    },
    calculateGroupDimensions: (items) => {
      const hasItems = items.length > 0;
      const minAndMaxX = hasItems ? getMinAndMaxBoundings(items, "x") : 0;
      const minAndMaxY = hasItems ? getMinAndMaxBoundings(items, "y") : 0;

      return {
        height: Math.round(minAndMaxY.max - minAndMaxY.min),
        width: Math.round(minAndMaxX.max - minAndMaxX.min),
      };
    },
    calculateStackedModulesPosition: (modules) => {
      modules.sort((a, b) => a.sort_position - b.sort_position);

      let x = modules[0].position.x;

      modules.forEach((m) => {
        m.position.x = x;
        x += m.dimensions.width;
      });
    },
    allowGrouping: () => {
      return selectionManager.selections().every((e) => {
        if (["group"].includes(e.type) || (e.type == "module" && !e.group_id))
          return true;

        return false;
      });
    },
    canSelectionsUnGroup: () => {
      return selectionManager.selections().every((e) => e.type === "group");
    },
    canSelectionsClone: () => {
      return selectionManager.selections().every((e) => {
        if (["group", "module", "wall"].includes(e.type)) return true;
        else return false;
      });
    },
    removeItems: (items) => {
      selectionManager.clear();

      const newRoom = { ...currentRoom };

      items.forEach((item) => {
        if (item.type === "group")
          newRoom.groups = newRoom.groups.filter((e) => e.id != item.id);
        else if (item.type === "module") {
          if (newRoom.modules.some((e) => e.id == item.id))
            newRoom.modules = newRoom.modules.filter((e) => e.id != item.id);
          else {
            for (const group of newRoom.groups)
              for (const module of group.modules)
                if (module.id == item.id)
                  group.modules = group.modules.filter((e) => e.id != item.id);
          }

          Object.values(newRoom.review_details).forEach((detail) => {
            detail.values.forEach((e) => {
              e.modules = e.modules.filter((id) => id !== item.id);
            });
          });
        }
      });

      setCurrentRoom(newRoom);
    },
  };

  const getMinAndMaxBoundings = (items, axis) => {
    const dimensionKey = axis === "x" ? "width" : "height";
    const maxBoundingItem = items.sort(
      (a, b) =>
        b.position[axis] +
        b.dimensions[dimensionKey] -
        (a.position[axis] + a.dimensions[dimensionKey])
    )[0];
    const min = items.sort((a, b) => a.position[axis] - b.position[axis])[0]
      .position[axis];
    const max =
      maxBoundingItem.position[axis] + maxBoundingItem.dimensions[dimensionKey];

    return { min, max };
  };

  const selectionManagerRef = useRef(selectionManager);

  useEffect(() => {
    selectionManagerRef.current = selectionManager;
  }, [selectionManager]);

  useEffect(() => {
    if (currentSelection.length && version == 1) {
      setCurrentTab("info");
    }
  }, [currentSelection]);

  const historyManager = {
    add: (action, oldValue, newValue) => {
      if (history.current.position < history.current.changes.length - 1) {
        // Trying to make a change when staying in an undone state.
        // Clear the change history after this point and add the new change.
        history.current.changes = [];
        history.current.position = -1;
      }

      history.current.position += 1;
      history.current.changes = history.current.changes.concat([
        { action: action, oldValue: oldValue, newValue, newValue },
      ]);
    },
    undo: () => {
      if (history.current.position === -1) {
        // Nothing to undo.
        return;
      }

      const change = history.current.changes[history.current.position];

      switch (change.action) {
        case "move":
          changeObjectPositions(change.oldValue);
          break;
      }

      history.current.position -= 1;
    },
    redo: () => {
      const change = history.current.changes[history.current.position + 1];

      if (change) {
        switch (change.action) {
          case "move":
            changeObjectPositions(change.newValue);
            break;
        }

        history.current.position += 1;
      }
    },
  };

  const getObjectKey = (item) => {
    return `${item.type}/${item.id}`;
  };

  const getObject = (key) => {
    const parts = key.split("/");
    const id = parseInt(parts[1]);

    switch (parts[0]) {
      case "group":
        return selectionManagerRef.current.findGroup(id);
      case "module":
        return selectionManagerRef.current.findModule(id);
      case "wall":
        return selectionManagerRef.current.findWall(id);
    }

    return null;
  };

  const changeObjectPositions = (positions) => {
    const itemsPayload = [];

    Object.keys(positions).forEach((key) => {
      const position = positions[key];
      const object = getObject(key);
      object.position.x = position.x;
      object.position.y = position.y;

      if (object.type === "module" && object.group_id) {
        const group = selectionManagerRef.current.findGroup(object.group_id);

        if (group.category === "BasicGroup") {
          const children = (group.modules || []).concat(group.walls || []);
          group.position = selectionManager.calculateGroupPosition(children);
          group.dimensions =
            selectionManager.calculateGroupDimensions(children);
        }
      }

      if (object.type !== "group")
        itemsPayload.push({
          id: object.id,
          type: object.type,
          position: object.position,
        });
    });

    forceUpdate();
    saveObjectPositions({
      projectId: projectId,
      roomId: currentRoomRef.current.id,
      items: itemsPayload,
    });
  };

  const handleKeyDown = (event) => {
    if (
      event.target instanceof HTMLInputElement ||
      event.target instanceof HTMLTextAreaElement
    ) {
      // User is possibly working on an input element. Don't apply shortcut.
      return;
    }

    if (event.key === "z" && (event.ctrlKey || event.metaKey)) {
      historyManager.undo();
    }

    if (event.key === "y" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      historyManager.redo();
    }
  };

  const isCurrentTab = (tab) => currentTab == tab;

  const mutateCurrentRoom = () => setCurrentRoom({ ...currentRoom });

  const setRoom = (room) => {
    room.groups.forEach((group) => {
      group.position = selectionManager.calculateGroupPosition(
        group.modules.concat(group.walls)
      );

      if (group.category === "BasicGroup")
        group.dimensions = selectionManager.calculateGroupDimensions(
          group.modules.concat(group.walls)
        );
    });

    setCurrentRoom(room);
  };

  const isListProject = projectSummary?.app_version === "2";

  return (
    <div className="fh">
      {notification.type && (
        <Notification
          color={notification.type === "error" ? "danger" : "success"}
          isOpen={isNotificationVisible}
          toggle={onDismissNotification}
        >
          {notification.message}
        </Notification>
      )}
      {!projectLoaded ? (
        <CustomizationSpinnerContainer top="200px">
          <Spinner />
        </CustomizationSpinnerContainer>
      ) : (
        currentRoom && (
          <div className="fh d-flex flex-column">
            <>
              <ProjectHeader
                projectId={projectId}
                showLegacyMaterials={projectSummary.show_legacy_materials}
                selectionManager={selectionManager}
                setCurrentTab={setCurrentTab}
                isCurrentTab={isCurrentTab}
                finalisedContexts={finalisedContexts}
                isAdmin={isAdmin}
                isDesigner={isDesigner}
                isViewer={isViewer}
                isOrderFinalised={isOrderFinalised}
                access={access}
                orderId={projectSummary.order_id}
                isOrderDraft={isOrderDraft}
                projectSummary={projectSummary}
                setProjectSummary={setProjectSummary}
                refreshProjectSummary={refreshProjectSummary}
                reloadRoom={reloadRoom}
                version={version}
                setVersion={setVersion}
                isListProject={isListProject}
              />
              <div className="d-flex flex-grow-1">
                {version == 1 ? (
                  <ProjectSummary
                    projectId={projectId}
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                    currentRoom={currentRoom}
                    projectSummary={projectSummary}
                    selectionManager={selectionManager}
                    currentGroup={currentGroup}
                    setCurrentGroup={setCurrentGroup}
                    loadRoom={loadRoom}
                    setProjectSummary={setProjectSummary}
                    currentTab={currentTab}
                    setIsReset={setIsReset}
                    hoverItem={hoverItem}
                    setHoverItem={setHoverItem}
                    isEq={isEq}
                    finalisedContexts={finalisedContexts}
                    isAdmin={isAdmin}
                    isDesigner={isDesigner}
                    isViewer={isViewer}
                    access={access}
                    isOrderFinalised={isOrderFinalised}
                    refreshProjectSummary={refreshProjectSummary}
                  />
                ) : (
                  <NewProjectSummary
                    projectId={projectId}
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                    currentRoom={currentRoom}
                    projectSummary={projectSummary}
                    selectionManager={selectionManager}
                    currentGroup={currentGroup}
                    setCurrentGroup={setCurrentGroup}
                    loadRoom={loadRoom}
                    setProjectSummary={setProjectSummary}
                    currentTab={currentTab}
                    setIsReset={setIsReset}
                    hoverItem={hoverItem}
                    setHoverItem={setHoverItem}
                    isEq={isEq}
                    finalisedContexts={finalisedContexts}
                    isAdmin={isAdmin}
                    isDesigner={isDesigner}
                    isViewer={isViewer}
                    access={access}
                    isOrderFinalised={isOrderFinalised}
                    refreshProjectSummary={refreshProjectSummary}
                    mutateCurrentRoom={mutateCurrentRoom}
                    currentSelection={currentSelection}
                    setCurrentTab={setCurrentTab}
                    setRoom={setRoom}
                    historyManager={historyManager}
                    isReset={isReset}
                    getObjectKey={getObjectKey}
                    isCurrentTab={isCurrentTab}
                    isListProject={isListProject}
                  />
                )}
                <div className={`flex-grow-1 fh ${isMobile && "d-none"}`}>
                  <Outlet
                    className="fh"
                    context={{
                      selectionManager,
                      historyManager,
                      projectId,
                      currentRoom,
                      currentGroup,
                      loadRoom,
                      isLoading,
                      setIsLoading,
                      currentSelection,
                      setCurrentSelection,
                      currentTab,
                      setCurrentTab,
                      isCurrentTab,
                      isReset,
                      setIsReset,
                      getObjectKey,
                      hoverItem,
                      setHoverItem,
                      isEq,
                      finalisedContexts,
                      isAdmin,
                      isDesigner,
                      isViewer,
                      isOrderFinalised,
                      isOrderDraft,
                      access,
                      loadProject,
                      lightsList,
                      projectSummary,
                      refreshProjectSummary,
                      projectSummary,
                      version,
                      isListProject,
                      setRoom,
                    }}
                  />
                </div>
              </div>
            </>
          </div>
        )
      )}
    </div>
  );
};

export default Project;
